import React, { useState } from 'react';
import { BokrugSpecialData, Modification } from '../api';
import { AncientOneCardProps, AncientOneSpecialData } from './AncientOneCard';
import { normalizeCardUri } from './Card';
import { GameDialog } from './GameDialog';
import './AncientOneDigitalCard.css';
import { digitalGameIconPath } from '../utils/gameIconArtwork';

const defenseNames: Record<string, string> = {
  PhysicalResistance: 'Physical Resistance',
  MagicalResistance: 'Magical Resistance',
  PhysicalImmunity: 'Physical Immunity',
  MagicalImmunity: 'Magical Immunity',
};

const defenseLabel = (modification: Modification): string =>
  defenseNames[modification.id] || modification.textDescription?.trim()
    || modification.id.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');

const signedRating = (rating: number) => rating > 0 ? `+${rating}` : `${rating}`;

// Keep identifying features visible when the square artwork becomes a wide mobile crop.
const artworkFocalPositions: Record<string, string> = {
  Cthulhu: '50% 15%',
  Hastur: '50% 30%',
  Yig: '50% 30%',
  Nyarlathotep: '50% 30%',
  Ithaqua: '50% 15%',
  Tsathoggua: '50% 30%',
  ShuddeMell: '50% 25%',
  Abhoth: '50% 70%',
  ChaugnarFaugn: '50% 15%',
  'Rhan-Tegoth': '50% 15%',
};

export const getAncientOneReworkImagePath = (name: string): string =>
  `/AncientOnes/rework/${normalizeCardUri(name)}.png`;

export const getAncientOnePreviewImagePath = (name: string): string =>
  `/AncientOnes/rework/small/${normalizeCardUri(name)}.jpg`;

const AncientOnePortrait: React.FC<{ name: string }> = ({ name }) => {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [artworkFailed, setArtworkFailed] = useState(false);
  return <div className="ancient-one-digital-portrait">
    {!failed && <><img src={getAncientOnePreviewImagePath(name)} alt={`${name} artwork`}
      loading="lazy" decoding="async" width={200} height={200}
      style={{ objectPosition: artworkFocalPositions[normalizeCardUri(name)] ?? '50% 50%' }}
      onError={() => { setFailed(true); setOpen(false); }} />
      <button type="button" className="ancient-one-artwork-open"
        aria-label={`View full artwork for ${name}`} aria-haspopup="dialog"
        onClick={() => { setArtworkFailed(false); setOpen(true); }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" aria-hidden="true" focusable="false">
          <circle cx="10" cy="10" r="6" /><path d="m14.5 14.5 6 6" />
        </svg>
      </button>
    </>}
    {open && <GameDialog title={`${name} artwork`} variant="artwork" onClose={() => setOpen(false)}>
      {artworkFailed ? <p role="alert">Artwork could not be loaded.</p>
        : <img className="ancient-one-artwork-full" src={getAncientOneReworkImagePath(name)}
          alt={`${name} full artwork`} decoding="async" width={1254} height={1254}
          onError={() => setArtworkFailed(true)} />}
    </GameDialog>}
  </div>;
};

export const AncientOneDigitalCard: React.FC<AncientOneCardProps> = ({
  ancientOne, isFinalBattle, ancientOneDamageDealt = 0, ancientOneDamageThreshold,
  worshippersDescription, powerDescription, attackDescription, startOfBattleDescription,
}) => {
  if (!ancientOne?.name) {
    return <p className="ancient-one-placeholder">Ancient One has not been revealed yet.</p>;
  }

  const { name, doomTrack, maximumDoomTrack, modifier } = ancientOne;
  const defenses = Array.from(new Set((ancientOne.combatModifications ?? []).map(defenseLabel).filter(Boolean)));
  const startOfBattle = (startOfBattleDescription ?? ancientOne.startOfBattleDescription)?.trim();
  const eyeCount = Math.max(maximumDoomTrack, doomTrack, 0);
  const damage = Math.max(0, ancientOneDamageDealt);
  const heartCount = Math.max(0, (ancientOneDamageThreshold ?? 0) - 1);
  const bokrug = name === 'Bokrug' ? ancientOne.specialData as BokrugSpecialData | undefined : undefined;

  return <article className="ancient-one-digital" aria-label={`${name} Ancient One`}>
    <header className="ancient-one-digital-header">
      <span className="ancient-one-digital-eyebrow">Ancient One</span>
      <h3>{name}</h3>
    </header>

    <div className="ancient-one-digital-doom">
      <div className="ancient-one-digital-doom-heading">
        <span>Doom track</span><strong>{doomTrack} / {maximumDoomTrack}</strong>
      </div>
      <div className="ancient-one-digital-doom-scroll" tabIndex={0}
        role="img" aria-label={`Doom track: ${doomTrack} of ${maximumDoomTrack}${doomTrack > maximumDoomTrack ? `, ${doomTrack - maximumDoomTrack} excess doom` : ''}`}>
        <div className="ancient-one-digital-eyes" aria-hidden="true">
          {Array.from({ length: eyeCount }, (_, index) => <span key={index}
            className={`ancient-one-digital-eye${index >= doomTrack ? ' ancient-one-digital-eye-empty' : ''}${index >= maximumDoomTrack ? ' ancient-one-digital-eye-excess' : ''}`} />)}
        </div>
      </div>
    </div>

    {isFinalBattle && heartCount >= 2 && <div className="ancient-one-digital-damage"
      aria-label={`Accumulated damage: ${damage}; doom removal threshold: ${ancientOneDamageThreshold}`}>
      <span>Accumulated damage: <strong>{damage}</strong></span>
      <div className="ancient-one-digital-hearts-scroll" tabIndex={0} role="img"
        aria-label={`${damage} accumulated damage; ${ancientOneDamageThreshold} damage required to remove a doom token`}>
        <span className="ancient-one-digital-hearts" aria-hidden="true">
          {Array.from({ length: heartCount }, (_, index) => <span key={index}
            className={`heart${index >= damage ? ' empty' : ''}`} />)}
        </span>
      </div>
    </div>}

    <div className="ancient-one-digital-overview">
      <AncientOnePortrait key={name} name={name} />
      <div className="ancient-one-digital-rules">
        <section className="ancient-one-digital-block" aria-label="Worshippers">
          <h4>Worshippers</h4>
          <p>{worshippersDescription ?? ancientOne.worshippersDescription}</p>
        </section>
        <section className="ancient-one-digital-block" aria-label="Power">
          <h4>Power</h4>
          <p>{powerDescription ?? ancientOne.powerDescription}</p>
        </section>
      </div>
    </div>

    <section className="ancient-one-digital-combat ancient-one-digital-block" aria-label="Combat">
      <h4>Combat</h4>
      <div className="ancient-one-digital-combat-stats">
        <div className="ancient-one-digital-rating">
          <span>Combat rating</span><strong>{signedRating(modifier)}</strong>
        </div>
        <div className="ancient-one-digital-defenses">
          <span>Defenses</span>
          <ul>{defenses.length ? defenses.map(defense => <li key={defense}>{defense}</li>) : <li>{name === 'Zhar' ? 'Special' : 'None'}</li>}</ul>
        </div>
      </div>
      {name === 'Nyogtha' && <p className="ancient-one-digital-combat-note">First investigator combat rating: {signedRating(modifier + 5)}</p>}
      {bokrug && !isFinalBattle && <p className="ancient-one-digital-combat-note">Combat rating at awakening: −X ({signedRating(-2 * bokrug.beingsOfIb.length)} with remaining Beings of Ib)</p>}
      {startOfBattle && <div className="ancient-one-digital-attack">
        <h5>Start of Battle</h5>
        <p>{startOfBattle}</p>
      </div>}
      <div className="ancient-one-digital-attack">
        <h5>Attack</h5>
        <p>{attackDescription ?? ancientOne.attackDescription}</p>
      </div>
    </section>

    {name === 'Zhar' && <img src={digitalGameIconPath('zhar')} alt="Zhar twin horror token" width={40} height={40} />}
    <AncientOneSpecialData ancientOne={ancientOne} isFinalBattle={isFinalBattle} detailsOnly />
  </article>;
};
